/**
 * Stocks widget: period tabs, segment tabs, search, sorting, calendar window
 * filter — all from data-* attributes already in the DOM, no network needed.
 *
 * The one exception is initLive(): the page itself sits behind a full-page
 * nginx cache, so the server-rendered price can be stale by the cache's TTL
 * for any given visitor. Polling cf-stocks/v1/live (explicitly excluded from
 * that cache, see class-cf-stocks-rest.php) is what actually keeps the price
 * current after the page has loaded.
 */

document.addEventListener('DOMContentLoaded', () => {
  const format = (value) =>
    value === '' ? '—' : `${Number(value) > 0 ? '+' : ''}${Number(value).toFixed(2)}%`;

  const signClass = (value) => {
    if (value === '') return 'is-flat';
    if (Number(value) > 0) return 'is-up';
    return Number(value) < 0 ? 'is-down' : 'is-flat';
  };

  const initTabs = (root) => {
    const tabs = root.querySelectorAll('[data-stocks-tab]');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const period = tab.dataset.stocksTab;
        root.dataset.period = period;

        tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        root.querySelectorAll('.stocks__row').forEach((row) => {
          const cell = row.querySelector('[data-stocks-change]');
          if (!cell) return;

          const value = row.dataset[period] ?? '';
          cell.textContent = format(value);
          cell.classList.remove('is-up', 'is-down', 'is-flat');
          cell.classList.add(signClass(value));
        });
      });
    });
  };

  // Segment and search both hide rows, so they share one pass — applying them
  // separately would let whichever ran last undo the other one.
  const applyFilter = (root) => {
    const segment = root.dataset.segment || '';
    const query = (root.dataset.query || '').trim();

    root.querySelectorAll('.stocks__row').forEach((row) => {
      const inSegment = segment === '' || row.dataset.segment === segment;
      const inQuery = query === '' || (row.dataset.search || '').includes(query);

      row.hidden = !(inSegment && inQuery);
    });
  };

  const initSegments = (root) => {
    const tabs = root.querySelectorAll('[data-stocks-segment]');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        root.dataset.segment = tab.dataset.stocksSegment;

        tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        applyFilter(root);
      });
    });
  };

  const initSearch = (root) => {
    const input = root.querySelector('[data-stocks-search]');
    if (!input) return;

    input.addEventListener('input', (e) => {
      root.dataset.query = e.target.value.toLowerCase();
      applyFilter(root);
    });
  };

  const initSort = (root) => {
    const buttons = root.querySelectorAll('[data-stocks-sort]');
    const tbody = root.querySelector('tbody');
    if (!buttons.length || !tbody) return;

    let currentKey = null;
    let asc = true;

    const syncButtons = () => {
      buttons.forEach((button) => {
        const active = button.dataset.stocksSort === currentKey;
        button.classList.toggle('is-sorted', active);
        button.classList.toggle('is-desc', active && !asc);
        button.setAttribute('aria-sort', active ? (asc ? 'ascending' : 'descending') : 'none');
      });
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.dataset.stocksSort;
        asc = key === currentKey ? !asc : true;
        currentKey = key;

        const rows = [...tbody.querySelectorAll('.stocks__row')];

        rows.sort((a, b) => {
          const av = a.dataset[key] ?? '';
          const bv = b.dataset[key] ?? '';
          const numeric = av !== '' && bv !== '' && !isNaN(av) && !isNaN(bv);
          const result = numeric
            ? Number(av) - Number(bv)
            : String(av).localeCompare(String(bv), 'sv');

          return asc ? result : -result;
        });

        rows.forEach((row) => tbody.appendChild(row));
        syncButtons();
      });
    });
  };

  // Nasdaq Stockholm isn't on EODHD's WebSocket coverage on any plan (only
  // US stocks/forex/crypto are) — this polls the 15-min-delayed REST feed
  // instead. Matches the intraday cron's own cadence; no point polling
  // faster than the source itself refreshes.
  const LIVE_POLL_MS = 2 * 60 * 1000;
  const LIVE_URL = '/wp-json/cf-stocks/v1/live';

  const formatPrice = (value) => {
    const [intPart, decPart] = Number(value).toFixed(2).split('.');
    return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}.${decPart}`;
  };

  const initLive = (root) => {
    const poll = () => {
      // nginx's fastcgi_cache ignores the route's Cache-Control: no-store and caches by full request URI
      // the timestamp keeps every poll on a fresh cache key so it can't serve back a stale HIT.
      const url = `${LIVE_URL}?_=${Date.now()}`;

      // Not a secret (it's sitting right here in the built bundle) — just
      // screens the route off from casual/accidental hits.
      fetch(url, { headers: { 'X-Cf-Stocks-Widget': 'supersecrettoken' } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const companies = (data && data.companies) || {};
          let hasAny = false;

          root.querySelectorAll('.stocks__row[data-ticker]').forEach((row) => {
            const quote = companies[row.dataset.ticker];
            const priceEl = row.querySelector('.stocks__price');

            if (!priceEl) return;

            if (!quote) {
              if (row.dataset.close) priceEl.textContent = formatPrice(row.dataset.close);
              return;
            }

            hasAny = true;
            priceEl.textContent = formatPrice(quote.price);

            if (quote.change_p !== null && quote.change_p !== undefined) {
              row.dataset.d1 = quote.change_p;

              const showsD1 = !root.dataset.period || root.dataset.period === 'd1';
              if (showsD1) {
                const cell = row.querySelector('[data-stocks-change]') || row.querySelector('.stocks__change[data-col="d1"]');
                if (cell) {
                  cell.textContent = format(row.dataset.d1);
                  cell.classList.remove('is-up', 'is-down', 'is-flat');
                  cell.classList.add(signClass(row.dataset.d1));
                }
              }
            }
          });

          root.classList.toggle('is-live', hasAny);
        })
        .catch(() => {
          console.error('Error polling stocks live data');
        });
    };

    poll();
    setInterval(poll, LIVE_POLL_MS);
  };

  const initCalendarWindow = (root) => {
    const tabs = root.querySelectorAll('[data-stocks-window]');
    if (!tabs.length) return;

    const apply = (windowKey) => {
      const limit = windowKey === 'week' ? 7 : windowKey === 'month' ? 30 : null;

      root.querySelectorAll('.stocks__row').forEach((row) => {
        if (null === limit) {
          row.hidden = false;
          return;
        }
        if (row.dataset.daysUntil === '') {
          row.hidden = true;
          return;
        }
        const days = Number(row.dataset.daysUntil);
        row.hidden = !(days >= 0 && days <= limit);
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const windowKey = tab.dataset.stocksWindow;
        root.dataset.window = windowKey;

        tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        apply(windowKey);
      });
    });
  };

  document.querySelectorAll('[data-stocks]').forEach((root) => {
    initTabs(root);
    initSegments(root);
    initSearch(root);
    initSort(root);
    initLive(root);
  });

  document.querySelectorAll('[data-stocks-calendar]').forEach((root) => {
    initCalendarWindow(root);
  });
});
